import {useState} from 'react';
import {Alert, Badge, Button, Card, Col, Container, Form, Row, Spinner, Table} from 'react-bootstrap';
import {getUserSessions, revokeSession, revokeUserSessions} from '../service/SessionService';

function formatDate(value) {
    if (!value) return '—';
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

export default function SessionAdminPage() {
    const [username, setUsername] = useState('');
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState(null);
    const [variant, setVariant] = useState('info');

    async function load(event) {
        event?.preventDefault();
        if (!username.trim()) return;
        setLoading(true);
        setMessage(null);
        try {
            setSessions(await getUserSessions(username.trim()));
        } catch (error) {
            setVariant('danger');
            setMessage(error.message || 'Unable to load sessions');
        } finally {
            setLoading(false);
        }
    }

    async function revokeAll() {
        if (!username.trim()) return;
        if (!window.confirm(`Force ${username.trim()} to sign out from all devices?`)) return;
        setLoading(true);
        try {
            const result = await revokeUserSessions(username.trim());
            setVariant('success');
            setMessage(`Revoked ${result.revoked_sessions || 0} active session(s).`);
            setSessions(await getUserSessions(username.trim()));
        } catch (error) {
            setVariant('danger');
            setMessage(error.message || 'Unable to revoke sessions');
        } finally {
            setLoading(false);
        }
    }

    async function revokeOne(sessionId) {
        setLoading(true);
        try {
            await revokeSession(sessionId);
            setVariant('success');
            setMessage('Device session revoked.');
            setSessions(await getUserSessions(username.trim()));
        } catch (error) {
            setVariant('danger');
            setMessage(error.message || 'Unable to revoke session');
        } finally {
            setLoading(false);
        }
    }

    return (
        <Container className="py-4 text-start">
            <Row className="mb-3">
                <Col>
                    <h3>Login Sessions</h3>
                    <p className="text-muted">Find a user and force sign out one device or every device. Revocation takes effect on Java and Go/chat APIs immediately.</p>
                </Col>
            </Row>
            {message ? <Alert variant={variant}>{message}</Alert> : null}
            <Card className="mb-3">
                <Card.Body>
                    <Form onSubmit={load} className="d-flex flex-wrap gap-2 align-items-end">
                        <Form.Group controlId="session-username" className="flex-grow-1">
                            <Form.Label>Username</Form.Label>
                            <Form.Control value={username} onChange={event => setUsername(event.target.value)} placeholder="Username" />
                        </Form.Group>
                        <Button className="text-nowrap" type="submit" disabled={loading || !username.trim()}>Load sessions</Button>
                        <Button className="text-nowrap" variant="danger" disabled={loading || !username.trim()} onClick={revokeAll}>Force sign out all</Button>
                    </Form>
                </Card.Body>
            </Card>
            {loading ? <Spinner animation="border" /> : null}
            {!loading && sessions.length > 0 ? (
                <Table responsive striped hover>
                    <thead>
                    <tr><th>Status</th><th>Client</th><th>Device</th><th>Created</th><th>Last active</th><th>Refresh expires</th><th></th></tr>
                    </thead>
                    <tbody>
                    {sessions.map(session => (
                        <tr key={session.session_id}>
                            <td><Badge bg={session.active ? 'success' : 'secondary'}>{session.active ? 'Active' : 'Inactive'}</Badge></td>
                            <td>{session.client_type || 'unknown'}</td>
                            <td>{session.device_name || session.device_id || '—'}</td>
                            <td>{formatDate(session.created_at)}</td>
                            <td>{formatDate(session.last_active_at)}</td>
                            <td>{formatDate(session.refresh_expires_at)}</td>
                            <td><Button size="sm" variant="outline-danger" disabled={!session.active} onClick={() => revokeOne(session.session_id)}>Sign out</Button></td>
                        </tr>
                    ))}
                    </tbody>
                </Table>
            ) : null}
        </Container>
    );
}
